import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import {
  getMyProfile,
  updateMyProfileService,
  updateEmployeeService,
  type EmployeeFormFields,
} from '../../services/MasterAdmin/EmployeeManagementService';

const departmentOptions = [
  'Management',
  'Sales',
  'Support',
  'Tech',
  'HR',
  'Operations',
  'Finance',
  'Marketing',
  'Legal',
  'Admin',
  'Content',
];

const roleOptions = [
  'MASTER_ADMIN',
  'SALES_EXECUTIVE',
  'ACCOUNTANT',
  'WRITER',
  'MARKETING',
  'SUPPORT',
  'TECH',
  'HR',
];

export type ProfileEditInitial = {
  id: string;
  name?: string;
  email?: string;
  role?: string;
  department?: string;
  phone?: string;
  qualification?: string | null;
  profile_image?: string | null;
};

type AdminProfileEditModalProps = {
  open: boolean;
  onClose: () => void;
  /** When set, edits that team member via employees API. Otherwise edits logged-in user via /profile. */
  member?: ProfileEditInitial | null;
  /** Allow changing role (Team Access only). Locked on own Profile. */
  allowRoleEdit?: boolean;
  /** `drawer` = slide-in panel from the right (team member page). */
  presentation?: 'modal' | 'drawer';
  createdByUserId: string;
  onSaved?: (user: Record<string, unknown>) => void;
};

const AdminProfileEditModal: React.FC<AdminProfileEditModalProps> = ({
  open,
  onClose,
  member = null,
  allowRoleEdit = false,
  presentation = 'modal',
  createdByUserId,
  onSaved,
}) => {
  const isSelf = !member;
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'MASTER_ADMIN',
    department: departmentOptions[0],
    phone: '',
    qualification: '',
  });
  const [profilePreview, setProfilePreview] = useState<string | null>(null);
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [removeProfileImage, setRemoveProfileImage] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const hydrate = async () => {
      try {
        let src: ProfileEditInitial | null = member;
        if (!src) {
          const res = await getMyProfile();
          src = res.user || null;
        }
        if (cancelled || !src) return;
        setForm({
          name: src.name || '',
          email: src.email || '',
          password: '',
          role: src.role || 'MASTER_ADMIN',
          department: src.department || departmentOptions[0],
          phone: src.phone || '',
          qualification: src.qualification || '',
        });
        setProfilePreview(src.profile_image || null);
        setProfileImageFile(null);
        setRemoveProfileImage(false);
      } catch {
        if (!cancelled) setToast('Failed to load profile.');
      }
    };

    hydrate();
    return () => {
      cancelled = true;
    };
  }, [open, member]);

  useEffect(() => {
    return () => {
      if (profilePreview?.startsWith('blob:')) {
        URL.revokeObjectURL(profilePreview);
      }
    };
  }, [profilePreview]);

  if (!open) return null;

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleProfileImagePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setToast('Profile image must be 2MB or smaller.');
      setTimeout(() => setToast(''), 2500);
      return;
    }
    if (profilePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(profilePreview);
    }
    setProfileImageFile(file);
    setRemoveProfileImage(false);
    setProfilePreview(URL.createObjectURL(file));
  };

  const clearProfileImage = () => {
    if (profilePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(profilePreview);
    }
    setProfileImageFile(null);
    setRemoveProfileImage(true);
    setProfilePreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setToast('');
    try {
      const payload: EmployeeFormFields & { password?: string } = {
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role,
        department: form.department,
        phone: form.phone.trim(),
        qualification: form.qualification.trim(),
        created_by: createdByUserId,
      };
      if (isSelf && form.password.trim()) {
        payload.password = form.password.trim();
      }

      let savedUser: Record<string, unknown> | undefined;
      if (isSelf) {
        const res = await updateMyProfileService(payload, {
          profileImageFile,
          removeProfileImage,
        });
        savedUser = res.data?.user;
      } else if (member?.id) {
        const res = await updateEmployeeService(
          member.id,
          payload,
          { profileImageFile, removeProfileImage }
        );
        savedUser = res.data?.user;
      }

      if (savedUser) {
        onSaved?.(savedUser);
        setToast('Profile updated successfully!');
        setTimeout(() => {
          setToast('');
          onClose();
        }, 800);
      } else {
        setToast('Failed to update profile.');
      }
    } catch (err: any) {
      setToast(err?.response?.data?.error || err?.response?.data?.message || 'Failed to update profile.');
    } finally {
      setLoading(false);
      setTimeout(() => setToast(''), 3000);
    }
  };

  const formFields = (
    <form className="space-y-5 px-4 py-5 sm:px-8 sm:py-7" onSubmit={handleSubmit}>
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Name
        </label>
        <input
          type="text"
          name="name"
          value={form.name}
          onChange={handleFormChange}
          required
          className="mt-1 block w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-2 shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Email
        </label>
        <input
          type="email"
          name="email"
          value={form.email}
          onChange={handleFormChange}
          required
          className="mt-1 block w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-2 shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        />
      </div>
      {isSelf && (
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            New password (optional)
          </label>
          <input
            type="password"
            name="password"
            value={form.password}
            onChange={handleFormChange}
            className="mt-1 block w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-2 shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            placeholder="Leave blank to keep current password"
          />
        </div>
      )}
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Role
        </label>
        {allowRoleEdit && !isSelf ? (
          <select
            name="role"
            value={form.role}
            onChange={handleFormChange}
            required
            className="mt-1 block w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-2 shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          >
            {roleOptions.map((role) => (
              <option key={role} value={role}>
                {role.replace('_', ' ')}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            value={form.role.replace('_', ' ')}
            disabled
            className="mt-1 block w-full rounded-lg border border-gray-200 bg-gray-100 px-4 py-2 text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
          />
        )}
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Department
        </label>
        <select
          name="department"
          value={form.department}
          onChange={handleFormChange}
          required
          className="mt-1 block w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-2 shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        >
          {departmentOptions.map((dep) => (
            <option key={dep} value={dep}>
              {dep}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Phone
        </label>
        <input
          type="text"
          name="phone"
          value={form.phone}
          onChange={handleFormChange}
          required
          className="mt-1 block w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-2 shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Profile Picture
        </label>
        <div className="flex items-center gap-4">
          {profilePreview ? (
            <img
              src={profilePreview}
              alt="Profile preview"
              className="h-16 w-16 rounded-full border border-gray-300 object-cover dark:border-gray-600"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-200 text-xs text-gray-500 dark:bg-gray-700 dark:text-gray-400">
              No photo
            </div>
          )}
          <div className="flex flex-col gap-2">
            <input
              type="file"
              accept="image/*"
              onChange={handleProfileImagePick}
              className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-3 file:py-1.5 file:text-primary-700 dark:text-gray-300 dark:file:bg-primary-900/40 dark:file:text-primary-300"
            />
            {profilePreview && (
              <button
                type="button"
                onClick={clearProfileImage}
                className="text-left text-sm text-red-600 hover:underline dark:text-red-400"
              >
                Remove photo
              </button>
            )}
          </div>
        </div>
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Qualification
        </label>
        <input
          type="text"
          name="qualification"
          value={form.qualification}
          onChange={handleFormChange}
          className="mt-1 block w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-2 shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          placeholder="e.g. MBA, B.Tech, Certified Accountant"
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        className="mt-2 w-full rounded-lg bg-gradient-to-r from-primary-600 to-primary-500 px-4 py-2 text-base font-semibold tracking-wide text-white shadow-md transition-all hover:from-primary-700 hover:to-primary-600 disabled:opacity-60"
      >
        {loading ? 'Saving…' : isSelf ? 'Update profile' : 'Update team member'}
      </button>
    </form>
  );

  const toastNode = toast ? (
    <div className="fixed top-6 right-6 z-[70] rounded bg-green-600 px-4 py-2 text-white shadow-lg">
      {toast}
    </div>
  ) : null;

  const title = isSelf ? 'Edit profile' : 'Edit team member';

  if (presentation === 'drawer') {
    return (
      <div className="fixed inset-0 z-[60] flex justify-end">
        {toastNode}
        <button
          type="button"
          aria-label="Close edit panel"
          className="absolute inset-0 bg-black/40"
          onClick={onClose}
        />
        <div className="relative flex h-full w-full max-w-md flex-col bg-white shadow-2xl dark:bg-gray-900 animate-[slideInRight_0.2s_ease-out]">
          <div className="flex shrink-0 items-center justify-between bg-gradient-to-r from-primary-600 to-primary-400 px-5 py-4 dark:from-primary-700 dark:to-primary-500">
            <h3 className="text-lg font-bold text-white">{title}</h3>
            <button
              type="button"
              className="text-white hover:text-gray-200 focus:outline-none"
              onClick={onClose}
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">{formFields}</div>
        </div>
        <style>{`
          @keyframes slideInRight {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center overflow-y-auto bg-black/40 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4">
      {toastNode}
      <div className="relative w-full max-w-lg max-h-[min(92dvh,calc(100vh-1rem))] animate-fade-in overflow-y-auto rounded-t-2xl bg-white p-0 shadow-2xl dark:bg-gray-900 sm:max-h-[90vh] sm:rounded-2xl">
        <div className="flex items-center justify-between rounded-t-2xl bg-gradient-to-r from-primary-600 to-primary-400 px-4 py-4 dark:from-primary-700 dark:to-primary-500 sm:px-8 sm:py-5">
          <h3 className="text-xl font-bold text-white">{title}</h3>
          <button
            type="button"
            className="text-white hover:text-gray-200 focus:outline-none"
            onClick={onClose}
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        {formFields}
      </div>
    </div>
  );
};

export default AdminProfileEditModal;
